import React, { useMemo } from 'react';
import { Box, Button, IconButton, InputAdornment, OutlinedInput, Select } from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import { analyticsUiTokens as T } from '../../constants/userAnalytics';
import { normalizeDomain } from '../../utils/analyticsMetrics';

const labelSx = {
  display: 'block',
  mb: 0.5,
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: T.slate[400],
};

const controlSx = {
  width: '100%',
  fontSize: 12,
  fontWeight: 500,
  color: T.slate[700],
  bgcolor: 'rgba(248,250,252,0.7)',
  borderRadius: '8px',
  '& fieldset': { borderColor: T.slate[200] },
  '&:hover': { bgcolor: T.slate[50] },
  '&:hover fieldset': { borderColor: T.slate[300] },
  '&.Mui-focused': { bgcolor: '#ffffff', boxShadow: '0 0 0 2px rgba(13,43,87,0.2)', '& fieldset': { borderColor: `${T.navy} !important`, borderWidth: '1px !important' } },
  '& .MuiSelect-select, & input': { py: '8px', fontSize: 12 },
};

const Badge = ({ tone = 'slate', children, ...rest }) => {
  const tones = {
    slate: { bgcolor: T.slate[100], color: T.slate[700], fontWeight: 500 },
    sky: { bgcolor: T.sky[50], color: T.sky[700], border: `1px solid ${T.sky[200]}`, fontWeight: 500 },
    indigo: { bgcolor: T.indigo[50], color: T.navy, fontWeight: 600 },
  };
  return (
    <Box component="span" {...rest} sx={{ px: 1, py: 0.25, borderRadius: '4px', fontSize: 11, ...tones[tone] }}>
      {children}
    </Box>
  );
};

/**
 * One global filter bar. `showEntitySpaceFilter` / `showEmailDomainFilter` come from
 * useAnalyticsScope: a marketing manager gets none of the controls — only the "Showing
 * analytics for" strip, which also states the fixed date window.
 *
 * There is no date control: the page always asks for the default period (last 30 days).
 *
 * `filters.emailDomain` is the raw text in the box; the page normalises and debounces it before
 * it reaches a request. Nothing here fetches.
 */
const AnalyticsFilterBar = ({
  filters, onChange, onReset,
  showEntitySpaceFilter, showEmailDomainFilter, restricted = false,
  entities = [], entitiesLoading = false,
  labels, dateBadge,
}) => {
  const allEntities = useLocalText('All Entities');
  const allSpaces = useLocalText('All Spaces');
  const domainPlaceholder = useLocalText('Filter by email domain...');
  const clearLabel = useLocalText('Clear email domain filter');
  const entityLabel = useLocalText('Entity Filter');
  const spaceLabel = useLocalText('Space Filter');
  const domainLabel = useLocalText('Email Domain');

  // Space options follow the chosen entity (the mock listed the same spaces under every one).
  const spaces = useMemo(() => {
    if (filters.entityId == null) return entities.flatMap((e) => e.spaces ?? []);
    return entities.find((e) => e.id === filters.entityId)?.spaces ?? [];
  }, [entities, filters.entityId]);

  const handleEntity = (e) => {
    const entityId = e.target.value === '' ? null : Number(e.target.value);
    const stillValid = filters.spaceId != null
      && (entityId == null || entities.find((en) => en.id === entityId)?.spaces?.some((s) => s.id === filters.spaceId));
    onChange({ entityId, spaceId: stillValid ? filters.spaceId : null });
  };

  const handleSpace = (e) => onChange({ spaceId: e.target.value === '' ? null : Number(e.target.value) });

  const modified =
    (showEntitySpaceFilter && (filters.entityId != null || filters.spaceId != null))
    || (showEmailDomainFilter && normalizeDomain(filters.emailDomain) !== '');

  // A marketing manager has no controls at all, so the bar is just the summary strip — no empty row above it.
  const hasLeftGroup = showEntitySpaceFilter || showEmailDomainFilter;

  return (
    <Box
      component="section"
      aria-label="Analytics filters"
      sx={{
        bgcolor: '#ffffff', border: `1px solid ${T.cardBorder}`, borderRadius: '12px', p: { xs: 1.75, sm: 2 }, boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {hasLeftGroup && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, minWidth: 0 }}>
          {showEntitySpaceFilter && (
            <>
              <Box>
                <Box component="label" htmlFor="ua-entity-filter" sx={labelSx}><LocalText>Entity Filter</LocalText></Box>
                <Select
                  native
                  size="small"
                  value={filters.entityId ?? ''}
                  onChange={handleEntity}
                  disabled={entitiesLoading}
                  inputProps={{ id: 'ua-entity-filter', 'aria-label': entityLabel }}
                  sx={controlSx}
                >
                  <option value="">{allEntities}</option>
                  {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </Select>
              </Box>
              <Box>
                <Box component="label" htmlFor="ua-space-filter" sx={labelSx}><LocalText>Space Filter</LocalText></Box>
                <Select
                  native
                  size="small"
                  value={filters.spaceId ?? ''}
                  onChange={handleSpace}
                  disabled={entitiesLoading}
                  inputProps={{ id: 'ua-space-filter', 'aria-label': spaceLabel }}
                  sx={controlSx}
                >
                  <option value="">{allSpaces}</option>
                  {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Select>
              </Box>
            </>
          )}
          {showEmailDomainFilter && (
            <Box>
              <Box component="label" htmlFor="ua-domain-filter" sx={labelSx}><LocalText>Email Domain</LocalText></Box>
              <OutlinedInput
                size="small"
                value={filters.emailDomain}
                onChange={(e) => onChange({ emailDomain: e.target.value })}
                placeholder={domainPlaceholder}
                inputProps={{ id: 'ua-domain-filter', 'aria-label': domainLabel, maxLength: 255, autoComplete: 'off' }}
                startAdornment={<InputAdornment position="start"><SearchOutlinedIcon sx={{ fontSize: 16, color: T.slate[400] }} /></InputAdornment>}
                endAdornment={filters.emailDomain ? (
                  <InputAdornment position="end">
                    <IconButton size="small" edge="end" aria-label={clearLabel} onClick={() => onChange({ emailDomain: '' })} sx={{ color: T.slate[400], '&:hover': { color: T.slate[600] } }}>
                      <CancelIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null}
                sx={controlSx}
              />
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          mt: hasLeftGroup ? 1.5 : 0, pt: hasLeftGroup ? 1.25 : 0,
          borderTop: hasLeftGroup ? `1px solid ${T.slate[100]}` : 'none',
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, fontSize: 11, color: T.slate[500],
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, flexWrap: 'wrap' }}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <FilterAltIcon sx={{ fontSize: 14, color: T.navy }} />
            <LocalText>Showing analytics for:</LocalText>
          </Box>
          {restricted ? (
            labels.space && <Badge data-testid="badge-space"><LocalText>Space:</LocalText> {labels.space}</Badge>
          ) : (
            <>
              <Badge data-testid="badge-entity">{filters.entityId == null ? allEntities : labels.entity}</Badge>
              <Badge data-testid="badge-space">{filters.spaceId == null ? allSpaces : labels.space}</Badge>
              {labels.domain && <Badge tone="sky" data-testid="badge-domain">@{labels.domain}</Badge>}
            </>
          )}
          <Badge tone="indigo" data-testid="badge-date">{dateBadge}</Badge>
        </Box>

        {modified && (
          <Button
            size="small"
            onClick={onReset}
            startIcon={<RestartAltOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, color: T.sky[700], minWidth: 0, p: 0.5, '&:hover': { color: T.navy, bgcolor: 'transparent' } }}
          >
            <LocalText>Reset Filters</LocalText>
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default AnalyticsFilterBar;
